import { ORDER_LINE_QUANTITY_MAX, defaultFulfilment, type Orderable, type OrderFulfilment, type OrderLinePriority } from '@his/shared';
import type { DraftLine, OrderKind, OrderScope } from './types';

export const IONISING_MODALITIES = ['xray', 'ct', 'mammography', 'fluoroscopy'];

export function draftLineOf(orderable: Orderable): DraftLine {
  return {
    chargeItemId: orderable.chargeItemId,
    refId: orderable.labTestId ?? orderable.labPanelId ?? orderable.radiologyStudyTypeId ?? null,
    code: orderable.code,
    name: orderable.name,
    priceMinor: orderable.priceMinor,
    priced: orderable.priced,
    inHouse: orderable.inHouse,
    hint: orderable.hint ?? null,
    quantity: 1,
    fulfilment: defaultFulfilment(orderable.inHouse),
    notes: '',
  };
}

export function clampQuantity(quantity: number): number {
  if (!Number.isFinite(quantity)) return 1;
  return Math.min(ORDER_LINE_QUANTITY_MAX, Math.max(1, Math.trunc(quantity)));
}

export function addToDraft(lines: readonly DraftLine[], orderable: Orderable): DraftLine[] {
  const existing = lines.find((line) => line.chargeItemId === orderable.chargeItemId);
  if (!existing) return [...lines, draftLineOf(orderable)];
  return lines.map((line) => (line === existing ? { ...line, quantity: clampQuantity(line.quantity + 1) } : line));
}

export function setQuantity(lines: readonly DraftLine[], chargeItemId: string, quantity: number): DraftLine[] {
  return lines.map((line) => (line.chargeItemId === chargeItemId ? { ...line, quantity: clampQuantity(quantity) } : line));
}

export function setFulfilment(lines: readonly DraftLine[], chargeItemId: string, fulfilment: OrderFulfilment): DraftLine[] {
  return lines.map((line) => (line.chargeItemId === chargeItemId ? { ...line, fulfilment: line.inHouse ? fulfilment : 'outside' } : line));
}

export function setLineNotes(lines: readonly DraftLine[], chargeItemId: string, notes: string): DraftLine[] {
  return lines.map((line) => (line.chargeItemId === chargeItemId ? { ...line, notes } : line));
}

export function removeFromDraft(lines: readonly DraftLine[], chargeItemId: string): DraftLine[] {
  return lines.filter((line) => line.chargeItemId !== chargeItemId);
}

export function inHouseTotalMinor(lines: readonly DraftLine[]): number {
  return lines.filter((line) => line.fulfilment === 'in_house').reduce((sum, line) => sum + line.priceMinor * line.quantity, 0);
}

export function ionisingInDraft(lines: readonly DraftLine[]): boolean {
  return lines.some((line) => IONISING_MODALITIES.includes((line.hint ?? '').toLowerCase()));
}

export function mayBePregnant(patient: { sex: string; dob: string | null; approximateAgeYears: number | null }, now = Date.now()): boolean {
  if (patient.sex !== 'female') return false;
  const years = patient.dob ? (now - new Date(patient.dob).getTime()) / (365.25 * 86_400_000) : patient.approximateAgeYears;
  if (years === null || years === undefined) return true;
  return years >= 12 && years <= 55;
}

export function initialOf(name: string): string {
  const first = name.trim().charAt(0).toLocaleUpperCase();
  return /\p{L}/u.test(first) ? first : '#';
}

export function groupByInitial<T extends { name: string }>(rows: readonly T[]): { initial: string; rows: T[] }[] {
  const groups: { initial: string; rows: T[] }[] = [];
  for (const row of rows) {
    const initial = initialOf(row.name);
    const last = groups[groups.length - 1];
    if (last && last.initial === initial) last.rows.push(row);
    else groups.push({ initial, rows: [row] });
  }
  return groups;
}

export interface DraftOrder {
  lines: readonly DraftLine[];
  priority: OrderLinePriority;
  indication: string;
  collectAt?: string;
  pregnancy?: '' | 'not_pregnant' | 'pregnant';
  pregnancyJustification?: string;
}

export const ADMISSION_NOTES_MAX = 600;

export function isAdmissionScope(scope: OrderScope): scope is { admissionId: string; encounterId?: undefined } {
  return !scope.encounterId && Boolean(scope.admissionId);
}

export function linesNotDoneHere(lines: readonly DraftLine[]): DraftLine[] {
  return lines.filter((line) => !line.inHouse);
}

export function placementPath(kind: OrderKind, scope?: OrderScope): string {
  if (scope && isAdmissionScope(scope)) return `/ipd/admissions/${scope.admissionId}/orders`;
  return kind === 'procedure' ? '/clinical-orders/procedure' : '/clinical-orders';
}

function admissionPlacementBody(kind: OrderKind, draft: DraftOrder, indication: string | undefined): Record<string, unknown> {
  return {
    priority: draft.priority,
    instructions: indication,
    details: {
      kind,
      items: draft.lines.map((line) => ({ chargeItemId: line.chargeItemId, quantity: line.quantity })),
      ...(kind === 'lab' && draft.collectAt ? { collectAt: new Date(draft.collectAt).toISOString() } : {}),
      notes: indication,
      ...(kind === 'radiology' ? { pregnancyChecked: Boolean(draft.pregnancy) } : {}),
      ...(kind === 'radiology' && draft.pregnancy === 'pregnant' ? { pregnant: true, pregnancyJustification: (draft.pregnancyJustification ?? '').trim() } : {}),
    },
  };
}

export function placementBody(kind: OrderKind, scope: OrderScope, patientId: string, draft: DraftOrder): Record<string, unknown> {
  const indication = draft.indication.trim() || undefined;
  if (isAdmissionScope(scope)) return admissionPlacementBody(kind, draft, indication?.slice(0, ADMISSION_NOTES_MAX));
  if (kind === 'procedure') {
    return {
      patientId,
      encounterId: scope.encounterId,
      priority: draft.priority,
      clinicalNotes: indication,
      items: draft.lines.map((line) => ({ chargeItemId: line.chargeItemId, quantity: line.quantity, fulfilment: line.fulfilment, notes: line.notes.trim() || undefined })),
    };
  }
  const anyInHouse = draft.lines.some((line) => line.fulfilment === 'in_house');
  return {
    patientId,
    ...(scope.encounterId ? { encounterId: scope.encounterId } : { admissionId: scope.admissionId }),
    type: kind,
    priority: draft.priority,
    clinicalNotes: indication,
    ...(kind === 'lab' && anyInHouse && draft.collectAt ? { collectAt: new Date(draft.collectAt).toISOString() } : {}),
    ...(kind === 'radiology' ? { pregnancyChecked: Boolean(draft.pregnancy) } : {}),
    ...(kind === 'radiology' && draft.pregnancy === 'pregnant' ? { pregnant: true, pregnancyJustification: (draft.pregnancyJustification ?? '').trim() } : {}),
    items: draft.lines.map((line) => ({
      chargeItemId: line.chargeItemId,
      ...(line.refId ? { refId: line.refId } : {}),
      label: line.name,
      quantity: line.quantity,
      fulfilment: line.fulfilment,
      notes: line.notes.trim() || undefined,
    })),
  };
}
